import config from '../../config';
import { handleErrors } from '../../js/helpers/handleErrors';
import { store } from '../../store';

/**
 * Fetch footfall data for a single date range and zone/location set.
 * Returns the total count (sum of all entries).
 * Uses the existing /stats/footfall endpoint with type=table, interval=day.
 */
export async function fetchFootfallTotal(
    dateFrom: string,
    dateTo: string,
    zones: number[],
    locations: number[]
): Promise<number> {
    const dataToSend: {
        date: { from: string; to: string }[];
        type: string;
        interval: string;
        zones?: number[];
        locations?: number[];
    } = {
        date: [{ from: dateFrom, to: dateTo }],
        type: 'table',
        interval: 'day',
    };

    if (zones.length > 0) {
        dataToSend.zones = zones;
    }
    if (locations.length > 0) {
        dataToSend.locations = locations;
    }

    // Need at least zones or locations
    if (zones.length === 0 && locations.length === 0) {
        return 0;
    }

    try {
        const res = await fetch(config.api.attendance, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${store.getState().userStore.token}`
            },
            credentials: 'include',
            body: JSON.stringify(dataToSend)
        });

        const handled = await handleErrors(res);
        const json = await handled.json();

        if (!Array.isArray(json)) return 0;

        let total = 0;
        json.forEach((entry: any) => {
            if (entry.data && Array.isArray(entry.data)) {
                entry.data.forEach((d: any) => {
                    total += parseInt(d.count || '0', 10);
                });
            }
        });

        return total;
    } catch {
        return 0;
    }
}

/**
 * Batch-fetch footfall for multiple date ranges in parallel.
 * Returns an array of totals in the same order as the input ranges.
 */
export async function fetchFootfallBatch(
    ranges: { dateFrom: string; dateTo: string }[],
    zones: number[],
    locations: number[]
): Promise<number[]> {
    const promises = ranges.map(r =>
        fetchFootfallTotal(r.dateFrom, r.dateTo, zones, locations)
    );
    return Promise.all(promises);
}

export interface IDailyEntry {
    date: string;  // YYYY-MM-DD
    count: number;
}

/**
 * Fetch footfall data broken down by day.
 * Returns an array of { date, count } entries.
 * Uses type=table, interval=day.
 */
export async function fetchFootfallDaily(
    dateFrom: string,
    dateTo: string,
    zones: number[],
    locations: number[]
): Promise<IDailyEntry[]> {
    const dataToSend: {
        date: { from: string; to: string }[];
        type: string;
        interval: string;
        zones?: number[];
        locations?: number[];
    } = {
        date: [{ from: dateFrom, to: dateTo }],
        type: 'table',
        interval: 'day',
    };

    if (zones.length > 0) dataToSend.zones = zones;
    if (locations.length > 0) dataToSend.locations = locations;

    if (zones.length === 0 && locations.length === 0) return [];

    try {
        const res = await fetch(config.api.attendance, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${store.getState().userStore.token}`
            },
            credentials: 'include',
            body: JSON.stringify(dataToSend)
        });

        const handled = await handleErrors(res);
        const json = await handled.json();

        if (!Array.isArray(json)) return [];

        // Aggregate counts by date across all zones/locations
        const dateMap = new Map<string, number>();
        json.forEach((entry: any) => {
            if (entry.data && Array.isArray(entry.data)) {
                entry.data.forEach((d: any) => {
                    const dateStr = (d.start || '').substring(0, 10); // "YYYY-MM-DD"
                    if (dateStr) {
                        dateMap.set(dateStr, (dateMap.get(dateStr) || 0) + parseInt(d.count || '0', 10));
                    }
                });
            }
        });

        const result: IDailyEntry[] = [];
        dateMap.forEach((count, date) => {
            result.push({ date, count });
        });
        result.sort((a, b) => a.date.localeCompare(b.date));
        return result;
    } catch {
        return [];
    }
}

export interface IHourlyEntry {
    hour: string;  // "HH:00:00"
    count: number;
}

/**
 * Fetch footfall data broken down by hour.
 * Returns an array of { hour, count } entries.
 * Uses type=XY, interval=hour, group=date so the API groups by hour across all dates.
 */
export async function fetchFootfallHourly(
    dateFrom: string,
    dateTo: string,
    zones: number[],
    locations: number[]
): Promise<IHourlyEntry[]> {
    const dataToSend: {
        date: { from: string; to: string }[];
        type: string;
        interval: string;
        group?: string;
        zones?: number[];
        locations?: number[];
    } = {
        date: [{ from: dateFrom, to: dateTo }],
        type: 'table',
        interval: 'hour',
    };

    if (zones.length > 0) dataToSend.zones = zones;
    if (locations.length > 0) dataToSend.locations = locations;

    if (zones.length === 0 && locations.length === 0) return [];

    try {
        const res = await fetch(config.api.attendance, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${store.getState().userStore.token}`
            },
            credentials: 'include',
            body: JSON.stringify(dataToSend)
        });

        const handled = await handleErrors(res);
        const json = await handled.json();

        if (!Array.isArray(json)) return [];

        // Aggregate counts by hour across all entries
        const hourMap = new Map<string, number>();
        json.forEach((entry: any) => {
            if (entry.data && Array.isArray(entry.data)) {
                entry.data.forEach((d: any) => {
                    // start format: "YYYY-MM-DD HH:mm:ss"
                    const startStr = d.start || '';
                    const timePart = startStr.substring(11, 13); // "HH"
                    if (timePart) {
                        const hourKey = timePart + ':00:00';
                        hourMap.set(hourKey, (hourMap.get(hourKey) || 0) + parseInt(d.count || '0', 10));
                    }
                });
            }
        });

        const result: IHourlyEntry[] = [];
        hourMap.forEach((count, hour) => {
            result.push({ hour, count });
        });
        result.sort((a, b) => a.hour.localeCompare(b.hour));
        return result;
    } catch {
        return [];
    }
}

/**
 * Response types for the yearlyanalytics endpoint.
 * This endpoint uses DB-level aggregation (GROUP BY weekday/hour/etc.)
 * instead of fetching all daily/hourly rows.
 */
export interface IWeekendAvgData {
    total: number;
    weekend_day_count: number;
    avg_per_weekend: number;
}

export interface IWeeklyAvgData {
    total: number;
    week_count: number;
    avg_per_week: number;
}

export interface IWeekdayAvgDailyData {
    total: number;
    weekday_count: number;
    avg_per_weekday: number;
}

export interface IWeekdayAvgEntry {
    weekday: number;    // DAYOFWEEK: 1=Sun, 2=Mon, ..., 7=Sat
    total: number;
    day_count: number;
    avg: number;
}

export interface IHourlyAvgEntry {
    hour_of_day: number;
    total: number;
    day_count: number;
}

export interface ITopDayEntry {
    day_date: string;   // YYYY-MM-DD
    total: number;
}

export interface IYearlyAnalyticsResponse {
    year: number;
    yearsBack: number;
    weekendAvg: Record<string, IWeekendAvgData>;    // keyed by year
    weeklyAvg: Record<string, IWeeklyAvgData>;      // keyed by year
    weekdayAvgDaily: Record<string, IWeekdayAvgDailyData>; // keyed by year
    weekdayAvg: Record<string, IWeekdayAvgEntry[]>; // keyed by year
    bihourlyAvg: Record<string, IHourlyAvgEntry[]>; // keyed by year
    topDays: ITopDayEntry[];
}

/**
 * Fetch yearly analytics from the dedicated endpoint that uses
 * DB-level aggregation (SQL GROUP BY) — much more efficient than
 * fetching all daily/hourly data and computing in JS.
 */
export async function fetchYearlyAnalytics(
    martId: number,
    year: number,
    yearsBack: number
): Promise<IYearlyAnalyticsResponse | null> {
    try {
        const res = await fetch(config.api.reportYearlyAnalytics, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${store.getState().userStore.token}`
            },
            credentials: 'include',
            body: JSON.stringify({
                mart: martId,
                year,
                yearsBack,
            })
        });

        const handled = await handleErrors(res);
        const json = await handled.json();

        if (json && json.year) {
            return json as IYearlyAnalyticsResponse;
        }
        return null;
    } catch {
        return null;
    }
}
